import React, { useEffect, useMemo, useState } from 'react';

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DEFAULT_DATE_RANGES = ['Today', 'Past 7 Days', 'This Month'];
const DEFAULT_TIMEZONES = ['Local time', 'UTC'];
const DEFAULT_STATUSES = ['All statuses', 'Online', 'Away', 'Offline'];
const EMPTY_HOURLY_TREND = Array(24).fill(0);
const EMPTY_WEEKLY_HEATMAP = Array.from({ length: 7 }, () => Array(24).fill(0));

function formatLastSeen(user, timezone) {
  let minutes = user.lastSeenMinutes;

  if (!Number.isFinite(minutes) && user.lastSeenAt) {
    minutes = Math.max(0, Math.round((Date.now() - new Date(user.lastSeenAt).getTime()) / 60000));
  }

  if (!Number.isFinite(minutes)) return '-';
  if (minutes <= 1) return 'Active now';
  if (minutes < 60) return `${minutes} min ago`;

  const date = new Date(Date.now() - minutes * 60 * 1000);

  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone === 'UTC' ? 'UTC' : undefined,
  }).format(date);
}

function formatDuration(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return '-';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours ? `${hours}h ${mins}m` : `${mins}m`;
}

function formatHour(hour) {
  if (typeof hour === 'string' && hour.includes(':')) return hour;
  return `${String(hour ?? 0).padStart(2, '0')}:00`;
}

function formatRefreshTime(date, timezone) {
  if (!date) return null;

  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timezone === 'UTC' ? 'UTC' : undefined,
  }).format(new Date(date));
}

function defaultStatusClass(status) {
  if (status === 'Online') return 'bg-success';
  if (status === 'Away') return 'bg-warning';
  if (status === 'Offline') return 'bg-danger';
  return 'bg-secondary';
}

function defaultHeatClass(value) {
  if (value <= 2) return 'bg-light text-muted';
  if (value <= 8) return 'bg-info bg-opacity-10 text-dark';
  if (value <= 20) return 'bg-info bg-opacity-25 text-dark';
  if (value <= 32) return 'bg-primary bg-opacity-50 text-white';
  return 'bg-primary text-white';
}

function UserAvatar({ user }) {
  const initials = user.name
    ?.split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2);

  if (user.avatarUrl) {
    return (
      <img
        alt=""
        className="rounded-circle border object-fit-cover"
        src={user.avatarUrl}
        style={{ width: 34, height: 34 }}
      />
    );
  }

  return (
    <div
      className="rounded-circle bg-light border text-dark d-flex align-items-center justify-content-center fw-semibold"
      style={{ width: 34, height: 34 }}
    >
      {initials || '?'}
    </div>
  );
}

/**
 * Presentational login-activity dashboard.
 *
 * The parent page owns API requests and passes users, trends, summaries and
 * action handlers into this component.
 */
export default function UserReportActivityDashboard({
  users = [],
  hourlyTrend = EMPTY_HOURLY_TREND,
  weeklyHeatmap = EMPTY_WEEKLY_HEATMAP,
  summary = {},
  dateRanges = DEFAULT_DATE_RANGES,
  dateRange,
  defaultDateRange = 'Past 7 Days',
  onDateRangeChange,
  timezones = DEFAULT_TIMEZONES,
  timezone,
  defaultTimezone = 'Local time',
  onTimezoneChange,
  lastUpdated,
  refreshIntervalMs = 30000,
  onRefresh,
  onMessage,
  onLogOut,
  statusClassFor = defaultStatusClass,
  heatClassFor = defaultHeatClass,
  title = 'Report Activity',
  subtitle = 'Track report usage and find low-traffic windows for maintenance.',
  isLoading = false,
  error = null,
  className = '',
}) {
  const [internalDateRange, setInternalDateRange] = useState(defaultDateRange);
  const [internalTimezone, setInternalTimezone] = useState(defaultTimezone);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [roleFilter, setRoleFilter] = useState('All roles');

  const selectedDateRange = dateRange ?? internalDateRange;
  const selectedTimezone = timezone ?? internalTimezone;
  const showActions = Boolean(onMessage || onLogOut);

  useEffect(() => {
    if (!onRefresh || !refreshIntervalMs) return undefined;

    const interval = window.setInterval(onRefresh, refreshIntervalMs);
    return () => window.clearInterval(interval);
  }, [onRefresh, refreshIntervalMs]);

  const roleOptions = useMemo(
    () => ['All roles', ...new Set(users.map((user) => user.role).filter(Boolean))],
    [users]
  );

  const filteredUsers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return users.filter((user) => {
      const searchableUser = `${user.name || ''} ${user.email || ''}`.toLowerCase();
      const matchesQuery = !normalizedQuery || searchableUser.includes(normalizedQuery);
      const matchesStatus = statusFilter === 'All statuses' || user.status === statusFilter;
      const matchesRole = roleFilter === 'All roles' || user.role === roleFilter;
      return matchesQuery && matchesStatus && matchesRole;
    });
  }, [query, roleFilter, statusFilter, users]);

  const normalizedHourlyTrend = HOURS.map((hour) => Number(hourlyTrend[hour]) || 0);
  const normalizedWeeklyHeatmap = DAYS.map((_, dayIndex) =>
    HOURS.map((hour) => Number(weeklyHeatmap[dayIndex]?.[hour]) || 0)
  );

  const maxHourlyLogins = Math.max(...normalizedHourlyTrend, 0);
  const derivedPeakHour = normalizedHourlyTrend.indexOf(maxHourlyLogins);
  const quietestHeatCell = normalizedWeeklyHeatmap
    .flatMap((row, dayIndex) => row.map((value, hour) => ({ day: DAYS[dayIndex], hour, value })))
    .sort((a, b) => a.value - b.value)[0];

  const activeUsers = summary.currentActiveUsers
    ?? users.filter((user) => user.status === 'Online').length;
  const dailyActiveUsers = summary.dailyActiveUsers
    ?? users.filter((user) => user.status !== 'Offline' || user.lastSeenMinutes < 24 * 60).length;
  const peakHour = summary.peakHour ?? derivedPeakHour;
  const lowestUsageWindow = summary.lowestUsageWindow
    ?? `${quietestHeatCell.day} ${String(quietestHeatCell.hour).padStart(2, '0')}:00`;

  function handleDateRangeChange(event) {
    const nextDateRange = event.target.value;
    setInternalDateRange(nextDateRange);
    onDateRangeChange?.(nextDateRange);
  }

  function handleTimezoneChange(event) {
    const nextTimezone = event.target.value;
    setInternalTimezone(nextTimezone);
    onTimezoneChange?.(nextTimezone);
  }

  return (
    <div className={`d-flex flex-column gap-3 ${className}`.trim()}>
      <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
        <div>
          <h3 className="mb-1">{title}</h3>
          {subtitle && <p className="text-muted mb-0">{subtitle}</p>}
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-start">
          <select
            aria-label="Activity date range"
            className="form-select form-select-sm"
            value={selectedDateRange}
            onChange={handleDateRangeChange}
            style={{ width: 145 }}
          >
            {dateRanges.map((range) => (
              <option key={range} value={range}>{range}</option>
            ))}
          </select>

          <select
            aria-label="Activity timezone"
            className="form-select form-select-sm"
            value={selectedTimezone}
            onChange={handleTimezoneChange}
            style={{ width: 130 }}
          >
            {timezones.map((zone) => (
              <option key={zone} value={zone}>{zone}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-danger mb-0">{error}</div>}

      <div className="row g-3">
        <div className="col-12 col-md-6 col-xl-3">
          <div className="border rounded-2 p-3 h-100">
            <div className="small text-muted">Current active users</div>
            <div className="fs-3 fw-semibold">{activeUsers}</div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="border rounded-2 p-3 h-100">
            <div className="small text-muted">Peak usage time</div>
            <div className="fs-3 fw-semibold">{formatHour(peakHour)}</div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="border rounded-2 p-3 h-100">
            <div className="small text-muted">Lowest usage window</div>
            <div className="fs-3 fw-semibold">{lowestUsageWindow}</div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="border rounded-2 p-3 h-100">
            <div className="small text-muted">Daily active users</div>
            <div className="fs-3 fw-semibold">{dailyActiveUsers}</div>
          </div>
        </div>
      </div>

      <section className="border rounded-2 p-3">
        <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-3">
          <div>
            <h5 className="mb-1">Popular Times Heatmap</h5>
            <p className="small text-muted mb-0">Darker blocks show busier day-and-hour combinations.</p>
          </div>
          <div className="d-flex gap-3 small text-muted">
            <span><span className="d-inline-block border bg-light me-1" style={{ width: 14, height: 14 }} /> Quiet</span>
            <span><span className={`d-inline-block me-1 ${heatClassFor(10)}`} style={{ width: 14, height: 14 }} /> Steady</span>
            <span><span className={`d-inline-block me-1 ${heatClassFor(40)}`} style={{ width: 14, height: 14 }} /> Busy</span>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-sm text-center align-middle mb-0" style={{ minWidth: 860 }}>
            <thead>
              <tr>
                <th className="text-start border-0" style={{ width: 72 }}>Day</th>
                {HOURS.map((hour) => (
                  <th key={hour} className="small fw-normal border-0">{String(hour).padStart(2, '0')}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {normalizedWeeklyHeatmap.map((row, dayIndex) => (
                <tr key={DAYS[dayIndex]}>
                  <th className="text-start small border-0">{DAYS[dayIndex]}</th>
                  {row.map((value, hour) => (
                    <td
                      key={hour}
                      className={heatClassFor(value)}
                      title={`${DAYS[dayIndex]} ${String(hour).padStart(2, '0')}:00 - ${value} logins`}
                      style={{ height: 34, cursor: 'help' }}
                    >
                      <span className="small">{value || ''}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="row g-3">
        <div className="col-12 col-xl-7">
          <section className="border rounded-2 p-3 h-100">
            <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-3">
              <div>
                <h5 className="mb-1">Active Now</h5>
                <p className="small text-muted mb-0">
                  {onRefresh && `Auto-refreshes every ${refreshIntervalMs / 1000} seconds. `}
                  {lastUpdated && `Last updated ${formatRefreshTime(lastUpdated, selectedTimezone)}.`}
                </p>
              </div>

              <div className="d-flex flex-wrap gap-2">
                <input
                  className="form-control form-control-sm"
                  type="search"
                  placeholder="Search name or email"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  style={{ width: 200 }}
                />

                <select
                  aria-label="Filter by status"
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  style={{ width: 140 }}
                >
                  {DEFAULT_STATUSES.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>

                <select
                  aria-label="Filter by role"
                  className="form-select form-select-sm"
                  value={roleFilter}
                  onChange={(event) => setRoleFilter(event.target.value)}
                  style={{ width: 125 }}
                >
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr className="small text-muted">
                    <th>User</th>
                    <th>Status</th>
                    <th>Last active</th>
                    <th>Session</th>
                    {showActions && <th className="text-end">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td className="text-center text-muted py-4" colSpan={showActions ? 5 : 4}>Loading activity...</td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td className="text-center text-muted py-4" colSpan={showActions ? 5 : 4}>No matching users.</td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <UserAvatar user={user} />
                            <div>
                              <div className="fw-semibold">{user.name}</div>
                              <div className="small text-muted">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`d-inline-block rounded-circle me-2 ${statusClassFor(user.status)}`}
                            style={{ width: 9, height: 9 }}
                          />
                          {user.status}
                        </td>
                        <td>{formatLastSeen(user, selectedTimezone)}</td>
                        <td>{formatDuration(user.sessionMinutes)}</td>
                        {showActions && (
                          <td className="text-end">
                            <div className="btn-group btn-group-sm" role="group" aria-label={`Actions for ${user.name}`}>
                              {onMessage && (
                                <button className="btn btn-outline-secondary" type="button" onClick={() => onMessage(user)}>
                                  Message
                                </button>
                              )}
                              {onLogOut && (
                                <button
                                  className="btn btn-outline-danger"
                                  type="button"
                                  disabled={user.status === 'Offline'}
                                  onClick={() => onLogOut(user)}
                                >
                                  Log out
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="col-12 col-xl-5">
          <section className="border rounded-2 p-3 h-100">
            <div className="mb-3">
              <h5 className="mb-1">Peak Hours</h5>
              <p className="small text-muted mb-0">Login volume by hour for {selectedDateRange.toLowerCase()}.</p>
            </div>
            <div className="d-flex align-items-end gap-1" style={{ height: 220 }}>
              {normalizedHourlyTrend.map((value, hour) => (
                <div
                  key={hour}
                  className="d-flex flex-column align-items-center flex-fill h-100 justify-content-end"
                  title={`${String(hour).padStart(2, '0')}:00 - ${value} logins`}
                >
                  <div
                    className="bg-primary rounded-top w-100"
                    style={{ minHeight: 4, height: `${maxHourlyLogins ? (value / maxHourlyLogins) * 100 : 0}%` }}
                  />
                  <div className="small text-muted mt-1" style={{ fontSize: '0.68rem' }}>{hour % 3 === 0 ? hour : ''}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
